import React, { useEffect, useState, useCallback } from 'react';
import { Search, Crown, X } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../lib/api';
import Pagination from '../../components/Pagination';
import { acquisitionSources } from '../../lib/constants';
import { Tag, TableCard, Th, Td, trClass, TableMessage, UserCell, fmtDate, StickyPager } from './ui';
import { friendlyError } from '../../lib/errorReporting';

const ACQUISITION_LABEL = Object.fromEntries(acquisitionSources.map((s) => [s.value, s.label]));

const LIMIT = 25;

// Google's cap on test users while the OAuth app is unverified.
const GMAIL_TEST_USER_CAP = 100;

// Quiet dot + text tags (see ui.js) instead of filled colored pills.
const PLAN_TAG = {
  pro: { tone: 'violet', label: 'Pro' },
  trial: { tone: 'sky', label: 'Trial' },
  free: { tone: 'muted', label: 'Sin acceso' },
  'sin perfil': { tone: 'rose', label: 'Sin perfil', title: 'Onboarding sin terminar' },
};

const URGENCY_ACTION = {
  upgrade: { tone: 'green', label: 'Actualizó' },
  referral: { tone: 'sky', label: 'Invitó a un amigo' },
  dismiss: { tone: 'muted', label: 'Siguió sin Pro' },
};

// Every filter the backend understands (admin.py admin_users). Empty = off.
const EMPTY_FILTERS = {
  plan_status: '', push: '', gmail: '', referral: '', goals: '', source: '', active: '',
  txn_from: '', txn_to: '', created_from: '', created_to: '', sort: 'created', referred_by: '',
};

const SELECTS = [
  { key: 'plan_status', label: 'Plan', options: [['', 'Todos'], ['pro', 'Pro'], ['trial', 'Trial'], ['free', 'Sin acceso'], ['sin perfil', 'Sin perfil']] },
  { key: 'push', label: 'Notificaciones push', options: [['', 'Todas'], ['on', 'Activas'], ['off', 'Apagadas']] },
  { key: 'gmail', label: 'Gmail', options: [['', 'Todos'], ['connected', 'Conectado'], ['reauth', 'Por reconectar'], ['none', 'Sin conectar'], ['requested', 'Pidió acceso'], ['enabled', 'Habilitado']] },
  { key: 'referral', label: 'Referidos', options: [['', 'Todos'], ['referred', 'Llegaron referidos'], ['not_referred', 'No referidos'], ['referrer', 'Refirieron a alguien']] },
  { key: 'goals', label: 'Metas', options: [['', 'Todos'], ['yes', 'Con metas'], ['no', 'Sin metas']] },
  { key: 'source', label: 'Fuente', options: [['', 'Todas'], ...acquisitionSources.map((s) => [s.value, s.label]), ['none', 'Sin responder']] },
  { key: 'active', label: 'Movimientos en el rango', options: [['', 'Todos'], ['yes', 'Con movimientos'], ['no', 'Sin movimientos']] },
  { key: 'sort', label: 'Ordenar por', options: [['created', 'Más recientes'], ['txns', 'Más movimientos'], ['last_txn', 'Último movimiento'], ['logins', 'Más logins'], ['referrals', 'Más referidos']] },
];

function toParams(f, page, search) {
  const p = { page, limit: LIMIT, search: search || undefined };
  Object.entries(f).forEach(([k, v]) => {
    if (!v || k === 'referral') return;
    p[k] = v;
  });
  if (f.referral === 'referred') p.referred = 'yes';
  if (f.referral === 'not_referred') p.referred = 'no';
  if (f.referral === 'referrer') p.referrer = 'yes';
  return p;
}

// One number; clicking it applies the filter that selects exactly those users.
function StatTile({ label, value, sub, onClick, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`text-left rounded-2xl border px-4 py-3 transition-colors ${active ? 'border-violet-400/50 bg-violet-500/10' : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]'} disabled:cursor-default`}
    >
      <p className="text-[12px] text-slate-500">{label}</p>
      <p className="mt-0.5 text-2xl font-semibold text-white tabular-nums">{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
    </button>
  );
}

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);
  const [gmailCount, setGmailCount] = useState(null);

  const load = useCallback(async (p, f, s) => {
    setLoading(true);
    try {
      const res = await api.getAdminUsers(toParams(f, p, s));
      setItems(res.items);
      setTotal(res.total);
      setStats(res.stats || null);
      if (res.gmail_allowlist_count !== undefined) setGmailCount(res.gmail_allowlist_count);
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudieron cargar los usuarios'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page, filters, search); }, [page, filters, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (key, value) => { setPage(1); setFilters((f) => ({ ...f, [key]: value })); };
  // Stat tiles toggle: clicking the active one clears it.
  const toggleFilter = (key, value) => setFilter(key, filters[key] === value ? '' : value);
  const clearFilters = () => { setPage(1); setSearch(''); setFilters(EMPTY_FILTERS); };
  const filtered = search || Object.entries(filters).some(([k, v]) => v && v !== EMPTY_FILTERS[k]);
  const referrerName = filters.referred_by && (items.find((u) => u.referred_by?.user_id === filters.referred_by)?.referred_by
    || stats?.top_referrers?.find((r) => r.user_id === filters.referred_by));

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    load(1, filters, search);
  };

  // Days of full Pro (e.g. Google's reviewer account). Extends the trial —
  // never recorded as a payment. See admin.py admin_grant_pro.
  const handleGrantPro = async (u) => {
    const input = window.prompt(`¿Cuántos días de Pro para ${u.email}?`, '30');
    if (input === null) return;
    const days = parseInt(input, 10);
    if (!days || days < 1 || days > 365) { toast.error('Escribe un número de días entre 1 y 365'); return; }
    try {
      const r = await api.grantAdminPro(u.user_id, days);
      toast.success(`Pro hasta el ${fmtDate(r.pro_access_until)}`);
      load(page, filters, search);
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo dar Pro'));
    }
  };

  // Gmail allowlist lives in the DB (admin.py gmail-access). With the Google
  // OAuth app "in production, unverified", this is the ONLY gate on who can
  // connect (Google's test-user list no longer applies) — and the counter is
  // what keeps us under Google's 100-user cap for unverified apps.
  const handleToggleGmail = async (u) => {
    try {
      if (u.gmail_access_enabled) {
        if (!window.confirm(`¿Quitar el acceso a Gmail de ${u.email}?`)) return;
        const r = await api.disableAdminGmail(u.user_id);
        setGmailCount(r.gmail_allowlist_count);
        toast.success('Acceso a Gmail quitado');
      } else {
        const r = await api.enableAdminGmail(u.user_id);
        setGmailCount(r.gmail_allowlist_count);
        toast.success(`Gmail habilitado para ${r.email}`, {
          description: 'Ya puede conectar su Gmail; le avisamos si lo había solicitado.',
        });
      }
      load(page, filters, search);
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo actualizar el acceso a Gmail'));
    }
  };

  const dash = <span className="text-slate-600">—</span>;
  const range = stats?.txn_range?.from || stats?.txn_range?.to
    ? `${stats.txn_range.from ? fmtDate(stats.txn_range.from) : 'inicio'} – ${stats.txn_range.to ? fmtDate(stats.txn_range.to) : 'hoy'}`
    : 'desde siempre';
  const inputClass = 'w-full bg-slate-900/70 border border-slate-700 rounded-xl h-9 px-2.5 text-[13px] text-white focus:outline-none focus:border-violet-500/50 [color-scheme:dark]';

  return (
    <div className="space-y-4 pb-2" data-testid="admin-users">
      {/* Search + filters */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
        <form onSubmit={handleSearchSubmit} className="relative w-full lg:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por email o nombre (Enter)"
            className="w-full bg-slate-900/70 border border-slate-700 rounded-xl h-10 pl-9 pr-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500/50"
          />
        </form>
        <div className="flex items-center gap-4 text-xs text-slate-500">
          {gmailCount !== null && (
            <p className={gmailCount >= GMAIL_TEST_USER_CAP * 0.9 ? 'text-amber-400' : ''} title="Correos habilitados para conectar Gmail mientras la app no está verificada por Google">
              Gmail habilitado: <span className="tabular-nums font-medium text-slate-300">{gmailCount}</span> / {GMAIL_TEST_USER_CAP}
            </p>
          )}
          {filtered && (
            <button onClick={clearFilters} className="inline-flex items-center gap-1 text-violet-300 hover:text-violet-200">
              <X className="w-3.5 h-3.5" /> Limpiar filtros
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="admin-users-filters">
        {SELECTS.map((s) => (
          <label key={s.key} className="space-y-1">
            <span className="block text-[11px] text-slate-500">{s.label}</span>
            <select value={filters[s.key]} onChange={(e) => setFilter(s.key, e.target.value)} className={inputClass}>
              {s.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        ))}
        <label className="space-y-1">
          <span className="block text-[11px] text-slate-500">Movimientos desde</span>
          <input type="date" value={filters.txn_from} onChange={(e) => setFilter('txn_from', e.target.value)} className={inputClass} />
        </label>
        <label className="space-y-1">
          <span className="block text-[11px] text-slate-500">Movimientos hasta</span>
          <input type="date" value={filters.txn_to} onChange={(e) => setFilter('txn_to', e.target.value)} className={inputClass} />
        </label>
        <label className="space-y-1">
          <span className="block text-[11px] text-slate-500">Registro desde</span>
          <input type="date" value={filters.created_from} onChange={(e) => setFilter('created_from', e.target.value)} className={inputClass} />
        </label>
        <label className="space-y-1">
          <span className="block text-[11px] text-slate-500">Registro hasta</span>
          <input type="date" value={filters.created_to} onChange={(e) => setFilter('created_to', e.target.value)} className={inputClass} />
        </label>
      </div>

      {filters.referred_by && (
        <p className="text-xs text-slate-400">
          Personas que llegaron por <span className="text-slate-200">{referrerName?.name || referrerName?.email || 'este usuario'}</span>
          <button onClick={() => setFilter('referred_by', '')} className="ml-2 text-violet-300 hover:text-violet-200">quitar</button>
        </p>
      )}

      {/* Stats over the filtered set — each tile applies its filter */}
      {stats && (
        <div className="space-y-2" data-testid="admin-users-stats">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            <StatTile label="Usuarios" value={stats.users}
              sub={`${stats.plans.pro || 0} Pro · ${stats.plans.trial || 0} trial · ${stats.plans.free || 0} sin acceso`} />
            <StatTile label="Push activo" value={stats.push_on} sub={`${stats.email_on} con correos activos`}
              active={filters.push === 'on'} onClick={() => toggleFilter('push', 'on')} />
            <StatTile label="Gmail conectado" value={stats.gmail_connected} sub={stats.gmail_reauth ? `${stats.gmail_reauth} por reconectar` : 'ninguno por reconectar'}
              active={filters.gmail === 'connected'} onClick={() => toggleFilter('gmail', 'connected')} />
            <StatTile label="Llegaron referidos" value={stats.referred} sub={`${stats.referrers} refirieron a alguien`}
              active={filters.referral === 'referred'} onClick={() => toggleFilter('referral', 'referred')} />
            <StatTile label="Con metas" value={stats.with_goals}
              active={filters.goals === 'yes'} onClick={() => toggleFilter('goals', 'yes')} />
            <StatTile label={`Con movimientos (${range})`} value={stats.active}
              sub={`${stats.txns} movimientos · ${stats.avg_txns_per_active} por persona activa`}
              active={filters.active === 'yes'} onClick={() => toggleFilter('active', 'yes')} />
          </div>
          {(stats.top_referrers?.length > 0 || Object.keys(stats.sources || {}).length > 0) && (
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-xs text-slate-400 px-1">
              {stats.top_referrers?.length > 0 && (
                <p>
                  <span className="text-slate-500">Quién trae más gente: </span>
                  {stats.top_referrers.map((r, i) => (
                    <React.Fragment key={r.user_id}>
                      {i > 0 && ', '}
                      <button onClick={() => setFilter('referred_by', r.user_id)} className="text-violet-300 hover:text-violet-200" title={`${r.rewarded} con recompensa`}>
                        {r.name || r.email} ({r.signups})
                      </button>
                    </React.Fragment>
                  ))}
                </p>
              )}
              <p>
                <span className="text-slate-500">Fuente: </span>
                {Object.entries(stats.sources).sort((a, b) => b[1] - a[1]).map(([k, n], i) => (
                  <React.Fragment key={k}>
                    {i > 0 && ' · '}
                    <button onClick={() => toggleFilter('source', k)} className={filters.source === k ? 'text-violet-300' : 'hover:text-slate-200'}>
                      {k === 'none' ? 'Sin responder' : (ACQUISITION_LABEL[k] || k)} {n}
                    </button>
                  </React.Fragment>
                ))}
              </p>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-slate-500">{total} {total === 1 ? 'usuario' : 'usuarios'}{filtered ? ' con estos filtros' : ''}</p>

      <TableCard>
        <table className="w-full text-[13px]">
          <thead>
            <tr>
              <Th>Usuario</Th>
              <Th>Plan</Th>
              <Th>Pro hasta</Th>
              <Th>Creado</Th>
              <Th>Último login</Th>
              <Th className="text-right">Movs.</Th>
              <Th>Último mov.</Th>
              <Th className="text-right">Metas</Th>
              <Th className="text-right">Logins</Th>
              <Th>Fuente</Th>
              <Th>Referido por</Th>
              <Th className="text-right">Refirió</Th>
              <Th>Gmail</Th>
              <Th>Avisos</Th>
              <Th>Popup urgencia</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <TableMessage colSpan={16}>Cargando…</TableMessage>
            ) : items.length === 0 ? (
              <TableMessage colSpan={16}>Sin resultados.</TableMessage>
            ) : items.map((u) => {
              const plan = PLAN_TAG[u.plan_status] || { tone: 'muted', label: u.plan_status };
              const urgency = URGENCY_ACTION[u.trial_urgency_last_action];
              return (
                <tr key={u.user_id} className={trClass}>
                  <Td className="max-w-[16rem]"><UserCell email={u.email} name={u.name} userId={u.user_id} /></Td>
                  <Td><Tag tone={plan.tone} title={plan.title}>{plan.label}</Tag></Td>
                  <Td className="text-xs text-slate-400 whitespace-nowrap">{u.pro_access_until ? fmtDate(u.pro_access_until) : dash}</Td>
                  <Td className="text-xs text-slate-400 whitespace-nowrap">{fmtDate(u.created_at)}</Td>
                  <Td className="text-xs text-slate-400 whitespace-nowrap">{fmtDate(u.last_login_at)}</Td>
                  <Td className="text-right tabular-nums text-slate-200">{u.txn_count || dash}</Td>
                  <Td className="text-xs text-slate-400 whitespace-nowrap">{fmtDate(u.last_transaction_at)}</Td>
                  <Td className="text-right tabular-nums text-slate-300">{u.goal_count || dash}</Td>
                  <Td className="text-right tabular-nums text-slate-300">{u.login_count ?? '—'}</Td>
                  <Td className="text-xs text-slate-400 whitespace-nowrap">{ACQUISITION_LABEL[u.acquisition_source] || u.acquisition_source || '—'}</Td>
                  <Td className="text-xs whitespace-nowrap max-w-[11rem] truncate">
                    {u.referred_by ? (
                      <button onClick={() => setFilter('referred_by', u.referred_by.user_id)} className="text-violet-300 hover:text-violet-200 truncate" title={u.referred_by.email}>
                        {u.referred_by.name || u.referred_by.email}
                      </button>
                    ) : dash}
                  </Td>
                  <Td className="text-right tabular-nums">
                    {u.referred_signups > 0 ? (
                      <button onClick={() => setFilter('referred_by', u.user_id)} className="text-emerald-400 font-medium hover:underline" title={`${u.referred_count} con recompensa (hicieron su primer movimiento)`}>
                        {u.referred_signups}
                      </button>
                    ) : dash}
                  </Td>
                  <Td>
                    {!u.gmail_connected && u.gmail_access_enabled ? (
                      <Tag tone="violet" title="En la lista de prueba — puede conectar Gmail">Habilitado</Tag>
                    ) : u.gmail_connected ? (
                      u.gmail_status === 'reauth_required'
                        ? <Tag tone="amber" title="Conectado, pero necesita reconectar (token vencido)">Reconectar</Tag>
                        : <Tag tone="green">Conectado</Tag>
                    ) : u.gmail_access_notified_at ? (
                      <Tag tone="violet" title={`Avisado el ${fmtDate(u.gmail_access_notified_at)} de que ya puede conectar — aún no lo hizo`}>Avisado</Tag>
                    ) : u.gmail_access_requested_at ? (
                      <Tag tone="sky" title={`Solicitó acceso el ${fmtDate(u.gmail_access_requested_at)} — app en Testing mode con Google`}>Solicitó acceso</Tag>
                    ) : dash}
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2.5">
                      <Tag
                        tone={u.push_subscribed ? 'green' : u.notifications_enabled ? 'amber' : 'muted'}
                        title={u.push_subscribed ? 'Push activo' : u.notifications_enabled ? 'Push activado pero sin suscripción viva' : 'Push desactivado'}
                      >Push</Tag>
                      <Tag tone={u.email_notifications_enabled ? 'green' : 'muted'} title={u.email_notifications_enabled ? 'Correos activados' : 'Correos desactivados'}>Correo</Tag>
                    </div>
                  </Td>
                  <Td>
                    {u.trial_urgency_shown_count > 0 ? (
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        {u.trial_urgency_acknowledged_at && urgency && <Tag tone={urgency.tone}>{urgency.label}</Tag>}
                        <span className="text-[11px] text-slate-500" title={u.trial_urgency_acknowledged_at ? `Respondió el ${fmtDate(u.trial_urgency_acknowledged_at)}` : undefined}>
                          {u.trial_urgency_shown_count}/5
                        </span>
                      </div>
                    ) : dash}
                  </Td>
                  <Td className="text-right whitespace-nowrap">
                    {!u.gmail_connected && (
                      <button
                        onClick={() => handleToggleGmail(u)}
                        data-testid={`gmail-access-${u.user_id}`}
                        className={`h-7 px-2.5 mr-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap active:scale-[0.97] ${
                          u.gmail_access_enabled ? 'text-slate-500 hover:text-rose-400 hover:bg-rose-500/10' : 'bg-white/[0.06] text-violet-300 hover:bg-white/10'
                        }`}
                      >
                        {u.gmail_access_enabled ? 'Quitar Gmail' : 'Habilitar Gmail'}
                      </button>
                    )}
                    {u.plan_status !== 'sin perfil' && (
                      <button
                        onClick={() => handleGrantPro(u)}
                        data-testid={`grant-pro-${u.user_id}`}
                        className="h-7 px-2.5 rounded-full text-xs font-medium bg-white/[0.06] text-violet-300 hover:bg-white/10 transition-colors whitespace-nowrap inline-flex items-center gap-1 active:scale-[0.97]"
                      >
                        <Crown className="w-3 h-3" /> Dar Pro
                      </button>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableCard>

      <StickyPager show={total > LIMIT}>
        <Pagination page={page} pageCount={Math.max(1, Math.ceil(total / LIMIT))} onPageChange={setPage} total={total} pageSize={LIMIT} />
      </StickyPager>
    </div>
  );
}
